import { useState } from 'react';
import { feedbackApi } from '@/api/feedback';
import { Button } from '@/components/ui/Button';
import { Grain } from '@/components/alianca/AliancaPrimitives';

interface PostChargeRatingProps {
  sessionId: string;
  reviewUrls?: Record<string, string>;
  /** Chamado ao enviar a nota OU ao dispensar. O Receipt grava o localStorage. */
  onClose: () => void;
}

// Nota >= PROMOTER_MIN é promotor → roteamos pra avaliação pública.
const PROMOTER_MIN = 9;

type Step = 'nps' | 'promoter' | 'detractor';

// Símbolo Bow vazado (branco sobre transparente) — mesmo asset que a Aliança
// usa em fundo escuro. Substitui o BowArc variant="white", que colapsava num
// anel branco sem identidade.
const BOW_VAZADA_SRC = '/logo/logo-bow-17.png';

// Metadados das plataformas de avaliação externa. A chave casa com
// `stations.external_review_urls` no backend; logos oficiais em /platforms/.
const PLATFORMS: Array<{ key: string; label: string; logo: string }> = [
  { key: 'google_maps', label: 'Google Maps', logo: '/platforms/google-maps.png' },
  { key: 'plugshare', label: 'PlugShare', logo: '/platforms/plugshare.png' },
  { key: 'carregados', label: 'Carregados', logo: '/platforms/carregados.webp' }
];

const WHATSAPP_URL =
  import.meta.env.VITE_SUPPORT_WHATSAPP_URL ?? 'https://wa.me/5527988231783';

/**
 * Card de NPS pós-recarga, exibido na tela de recibo quando a sessão conclui.
 *
 * Passo 1 (nps): nota 0–10 ("o quanto você recomendaria a Bow?").
 *  - 9–10 (promotor): envia na hora e mostra grid de logos das plataformas de
 *    avaliação externa (variante "C · Logos em destaque" do redesign — visual,
 *    quase sem texto, saída quieta "Agora não" separada por divisor).
 *  - 0–8: abre comentário opcional + atalho de suporte; envia score+comentário
 *    juntos no "Enviar" (o backend é idempotente e não atualiza comentário
 *    num segundo POST, então mandamos de uma vez só).
 *
 * Bottom-sheet (mobile) / modal centrado (sm+), espelhando o AdesaoModal.
 */
export function PostChargeRating({
  sessionId,
  reviewUrls,
  onClose
}: PostChargeRatingProps) {
  const [step, setStep] = useState<Step>('nps');
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availablePlatforms = PLATFORMS.filter((p) => reviewUrls?.[p.key]);
  const hasPlatforms = availablePlatforms.length > 0;

  async function submit(value: number, withComment: string | null) {
    setBusy(true);
    setError(null);
    try {
      await feedbackApi.submitNps(sessionId, {
        score: value,
        comment: withComment
      });
      return true;
    } catch {
      // NPS é best-effort: não bloqueia o cliente. Mostra erro discreto e
      // deixa ele tentar de novo ou fechar.
      setError('Não foi possível enviar agora. Tente de novo ou feche.');
      setBusy(false);
      return false;
    }
  }

  async function handlePickScore(value: number) {
    if (busy) return;
    setScore(value);
    if (value >= PROMOTER_MIN) {
      const ok = await submit(value, null);
      setBusy(false);
      // Sempre mostra o passo de promotor: com links externos quando houver,
      // ou só um agradecimento quando o eletroposto ainda não tem links.
      if (ok) setStep('promoter');
    } else {
      setStep('detractor');
    }
  }

  async function handleSubmitDetractor() {
    if (score === null) return;
    const ok = await submit(score, comment.trim() || null);
    setBusy(false);
    if (ok) onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-bow-royal/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-t-[28px] bg-bow-cream shadow-deep sm:mb-8 sm:max-h-[80vh] sm:rounded-[28px]">
        {/* Header dark com gradient + símbolo Bow vazado */}
        <div
          className="relative overflow-hidden text-white"
          style={{
            background:
              'linear-gradient(135deg, #001B74 0%, #1d2a8c 55%, #00C072 145%)'
          }}
        >
          <Grain opacity={0.1} />
          <div
            className="pointer-events-none absolute"
            style={{ top: -64, right: -58, opacity: 0.12 }}
          >
            <img src={BOW_VAZADA_SRC} alt="" style={{ height: 210, width: 'auto' }} />
          </div>
          <div className="relative px-6 pb-5 pt-5">
            <img src={BOW_VAZADA_SRC} alt="Bow" style={{ height: 30, width: 'auto' }} />
            <h2
              className="mt-4 text-bow-cream"
              style={{
                fontFamily: 'Chillax, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 28,
                letterSpacing: '-0.03em',
                lineHeight: 1.05
              }}
            >
              {step === 'nps' && 'Como foi sua recarga?'}
              {step === 'promoter' && 'Que demais!'}
              {step === 'detractor' && 'Obrigado pela nota'}
            </h2>
            <p
              className="mt-2 text-sm"
              style={{ lineHeight: 1.45, color: 'rgba(255,255,255,0.88)', maxWidth: 300 }}
            >
              {step === 'nps' &&
                'De 0 a 10, o quanto você recomendaria a Bow para um amigo?'}
              {step === 'promoter' &&
                (hasPlatforms
                  ? 'Ajude outros motoristas a achar a Bow.'
                  : 'Ficamos felizes que você curtiu a experiência.')}
              {step === 'detractor' &&
                'Conta pra gente o que podemos melhorar (opcional).'}
            </p>
          </div>
        </div>

        {/* Body */}
        <div
          className={`flex-1 overflow-y-auto ${
            step === 'promoter' && hasPlatforms
              ? 'px-[22px] pb-[18px] pt-5'
              : 'px-6 py-5'
          }`}
        >
          {step === 'nps' && (
            <NpsScale selected={score} disabled={busy} onPick={handlePickScore} />
          )}

          {step === 'promoter' && !hasPlatforms && (
            <p className="text-sm leading-relaxed text-bow-royal/70">
              Sua nota foi registrada. Obrigado por recarregar com a Bow!
            </p>
          )}

          {step === 'promoter' && hasPlatforms && (
            <div className="grid grid-cols-3 gap-[11px]">
              {availablePlatforms.map((p) => (
                <a
                  key={p.key}
                  href={reviewUrls?.[p.key]}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-col items-center gap-[9px] rounded-[20px] border-[1.5px] border-bow-royal/10 bg-white px-2 pb-3.5 pt-[18px] shadow-[0_2px_8px_rgba(0,27,116,0.05)] transition hover:-translate-y-[3px] hover:border-bow-royal/[0.18] hover:shadow-[0_12px_26px_rgba(0,27,116,0.12)] active:scale-[0.985]"
                >
                  <img
                    src={p.logo}
                    alt=""
                    className="h-[58px] w-[58px] rounded-[13px] object-contain"
                  />
                  <span className="text-center text-[13px] font-semibold leading-[1.2] text-bow-royal">
                    {p.label}
                  </span>
                </a>
              ))}
            </div>
          )}

          {step === 'detractor' && (
            <div className="flex flex-col gap-3">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="O que aconteceu? (opcional)"
                disabled={busy}
                className="w-full resize-none rounded-2xl border border-bow-royal/15 bg-white px-4 py-3 text-sm text-bow-royal placeholder:text-bow-royal/40 focus:border-bow-royal/40 focus:outline-none"
              />
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="text-center text-[13px] font-semibold text-bow-green underline-offset-2 hover:underline"
              >
                Prefere falar com o suporte? (WhatsApp)
              </a>
            </div>
          )}

          {error && <p className="mt-3 text-center text-sm text-bow-red">{error}</p>}
        </div>

        {/* Footer */}
        {step === 'nps' && <QuietExit disabled={busy} onClick={onClose} />}

        {step === 'promoter' &&
          (hasPlatforms ? (
            <QuietExit onClick={onClose} />
          ) : (
            <div
              className="border-t border-bow-royal/10 bg-white px-6 pt-4"
              style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
            >
              <Button variant="primary" size="md" onClick={onClose}>
                Concluir
              </Button>
            </div>
          ))}

        {step === 'detractor' && (
          <div
            className="border-t border-bow-royal/10 bg-white px-6 pt-4"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
          >
            <div className="grid grid-cols-2 gap-2">
              <Button variant="ghost" size="md" disabled={busy} onClick={onClose}>
                Pular
              </Button>
              <Button
                variant="primary"
                size="md"
                disabled={busy}
                onClick={handleSubmitDetractor}
              >
                {busy ? 'Enviando…' : 'Enviar'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface QuietExitProps {
  disabled?: boolean;
  onClick: () => void;
}

/**
 * Saída quieta atrás de um divisor — demove o "fechar" pra não competir com
 * as ações de avaliação (padrão QuietConcluir do redesign).
 */
function QuietExit({ disabled, onClick }: QuietExitProps) {
  return (
    <div
      className="border-t border-bow-royal/10 bg-white px-6 pt-3"
      style={{ paddingBottom: 'max(14px, env(safe-area-inset-bottom))' }}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className="h-10 w-full rounded-full text-[14.5px] font-semibold text-bow-royal/[0.52] transition hover:bg-bow-royal/5 hover:text-bow-royal disabled:cursor-not-allowed disabled:opacity-50"
      >
        Agora não
      </button>
    </div>
  );
}

interface NpsScaleProps {
  selected: number | null;
  disabled: boolean;
  onPick: (value: number) => void;
}

function NpsScale({ selected, disabled, onPick }: NpsScaleProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-11">
        {Array.from({ length: 11 }, (_, n) => {
          const isSelected = selected === n;
          const tone =
            n >= 9 ? '#00C072' : n >= 7 ? '#9ca3af' : '#EE263E';
          return (
            <button
              key={n}
              type="button"
              disabled={disabled}
              onClick={() => onPick(n)}
              className="flex h-11 items-center justify-center rounded-xl border-[1.5px] text-sm font-semibold transition disabled:cursor-not-allowed"
              style={
                isSelected
                  ? { background: tone, borderColor: tone, color: '#fff' }
                  : {
                      background: '#fff',
                      borderColor: 'rgba(0,27,116,0.12)',
                      color: '#001B74'
                    }
              }
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between px-1 text-[11px] font-medium text-bow-royal/45">
        <span>Nada provável</span>
        <span>Muito provável</span>
      </div>
    </div>
  );
}
